import type { Gender, GenderFilter, ReportReason, Scope, SearchPrefs } from '@repo/shared/domain';

export const GENDER_LABELS: Record<Gender, string> = {
  female: 'Frau',
  male: 'Mann',
  couple: 'Paar',
};

export const GENDER_FILTER_LABELS: Record<GenderFilter, string> = {
  any: 'Alle',
  female: 'Frauen',
  male: 'Männer',
  couple: 'Paare',
};

export const SCOPE_LABELS: Record<Scope, string> = {
  global: 'Weltweit',
  national: 'Mein Land',
  local: 'Umkreis',
};

export const REPORT_REASON_LABELS: Record<ReportReason, string> = {
  nudity: 'Nacktheit oder sexuelle Inhalte',
  minor: 'Person wirkt minderjährig',
  harassment: 'Belästigung, Beleidigung, Hass',
  spam: 'Spam oder Werbung',
  other: 'Etwas anderes',
};

const PEOPLE: Record<Exclude<GenderFilter, 'any'>, string> = {
  female: 'Frauen',
  male: 'Männer',
  couple: 'Paare',
};

const numbers = new Intl.NumberFormat('de-DE');
const regions = new Intl.DisplayNames(['de'], { type: 'region' });

export const formatCount = (count: number): string => numbers.format(count);

export const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

const dates = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' });

/** Zeitstempel in Millisekunden, z. B. "13.10.2026" */
export const formatDate = (ms: number): string => dates.format(ms);

export function countryName(code: string | null): string {
  if (!code) return 'Unbekanntes Land';
  try {
    return regions.of(code) ?? code;
  } catch {
    return code;
  }
}

/** z. B. "im Umkreis von 40 km · nur Frauen" */
export function searchSummary(prefs: SearchPrefs): string {
  const region =
    prefs.scope === 'local'
      ? `im Umkreis von ${prefs.radiusKm} km`
      : prefs.scope === 'national'
        ? 'in deinem Land'
        : 'weltweit';
  return prefs.genderFilter === 'any' ? region : `${region} · nur ${PEOPLE[prefs.genderFilter]}`;
}

/** Kurzform für den Filter-Button, z. B. "40 km · Frauen" */
export function filterChipLabel(prefs: SearchPrefs): string {
  const region = prefs.scope === 'local' ? `${prefs.radiusKm} km` : SCOPE_LABELS[prefs.scope];
  return prefs.genderFilter === 'any'
    ? region
    : `${region} · ${GENDER_FILTER_LABELS[prefs.genderFilter]}`;
}

/** Gesprächsdauer als mm:ss, ab einer Stunde mit Stunden davor. */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const pad = (value: number) => String(value).padStart(2, '0');
  const hours = Math.floor(total / 3_600);
  const minutes = Math.floor(total / 60) % 60;
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(total % 60)}`
    : `${minutes}:${pad(total % 60)}`;
}

/** Wie die Leitung beschrieben wird. */
export const QUALITY_LABELS = {
  good: 'Leitung gut',
  fair: 'Leitung wackelig',
  poor: 'Leitung schwach',
} as const;
